import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography
} from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { editMedia4, mediasRef } from '../../api/firebase/firestore/media';
import { mediaCaseShema } from '../../api/firebase/firestore/media/validation';
import { usersRef } from '../../api/firebase/firestore/user';
import { mainKpi } from '../../constants/CheckboxList';
import {
  black,
  blackText,
  blueGrayText,
  cardBorder,
  gray,
  primaryButton,
  primaryButtonBackground,
  primarycolor
} from '../../constants/Colors';
import {
  buttonFont,
  cardContentPadding,
  cardSize,
  formWideMb,
  mypageHeader,
  mypageHeaderTitle,
  textFontSize,
  textMb,
  titleFontSize,
  titleMb
} from '../../constants/Location';
import { publisherMediaRegisterEditSteps } from '../../constants/RegisterStep';
import { useIndustry } from '../../constants/Select';
import { select } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

import CustomCheckbox from './CustomCheckbox';
import CustomLoading from './CustomLoading';
import CustomSelectField from './CustomSelectField';
import CustomSnackbar from './CustomSnackbar';
import CustomStepper from './CustomStepper';
import CustomTextfield from './CustomTextfield';

import type { MediaType } from '../../api/firebase/firestore/media/mediaType';

const styles = {
  buttonFont,
  cardBorder,
  cardContentPadding,
  cardSize,
  circle: {
    bgcolor: '#DBDBE0',
    borderRadius: '50%',
    height: '64px',
    marginBottom: '10px',
    width: '64px'
  },
  flex: {
    display: 'flex'
  },
  formWideMb,
  grayPlaceholderText: {
    color: '#757575',
    fontFamily: 'Noto Sans JP,sans-serif',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '1.5',
    marginBottom: '10px'
  },
  isRequiredText: {
    color: '#BD0017',
    fontFamily: 'Noto Sans JP,sans-serif',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '1.5',
    margin: '0',
    marginLeft: '10px'
  },
  issueDescription: {
    color: blueGrayText,
    fontSize: '14px',
    marginBottom: '24px'
  },
  label: {
    fontSize: '12px'
  },
  mypageHeader,
  mypageHeaderTitle,
  radiomarginLeft: {
    marginLeft: '5px'
  },
  required: {
    color: primarycolor,
    fontSize: '12px',
    marginLeft: '10px'
  },
  skipButton: {
    height: '36px',
    width: '140px'
  },
  submit: {
    marginBottom: 0,
    marginTop: '32px',
    padding: 0
  },
  submitButton: {
    '@media screen and (max-width: 960px)': {
      height: '42px'
    },
    height: '42px'
  },
  submitCol: {
    columnGap: '8px'
  },
  text: {
    blackText,
    fontSize: '14px',
    marginBottom: '10px'
  },
  textFontSize,
  textMb,
  title: {
    color: blackText,
    fontSize: titleFontSize
  },
  titleMb,
  undoButton: {
    '&:hover': {
      borderColor: gray,
      opacity: 0.7
    },
    border: `1px solid ${gray}`,
    color: gray,
    height: '42px',
    marginRight: '16px'
  },
  uploadButton: {
    '&:hover': {
      backgroundColor: black
    },
    backgroundColor: black,
    height: '36px',
    marginLeft: '11px',
    minWidth: '157px'
  }
};

export interface RegisterMediaCaseType {
  industriesNum1: number;
  industriesOther1: string | null;
  industriesPercentage1: number;
  industriesNum2: number;
  industriesOther2: string | null;
  industriesPercentage2: number;
  industriesNum3: number;
  industriesOther3: string | null;
  industriesPercentage3: number;
  mainKpi: boolean[];
  mainKpiOther: string | null;
  clientsBenefits: string;
  continuousFactors1: string;
  continuousFactors2: string;
  continuousFactors3: string;
  mediaMenuUrl: string;
}

const MediaRegisterCaseEditCard = (): React.ReactElement => {
  const { t } = useTranslation();
  const steps = publisherMediaRegisterEditSteps();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useContext(AuthContext);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const industryList = useIndustry();
  const [mediaCompanyId, setMediaCompanyId] = useState<string>('');

  useEffect(() => {
    if (!currentUser) {
      setOpen(true);
      setErrorText(t('form.loginError'));
      setTimeout(() => {
        navigate('/auth/login');
      }, 6000);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    void (async () => {
      if (mediaCompanyId) {
        const userRef = doc(usersRef, currentUser?.uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();
        if (userData) {
          const userCompany = userData.companyId as string;
          if (userCompany !== mediaCompanyId) {
            navigate('/auth/login');
          }
        }
      }
    })();
  }, [mediaCompanyId]);

  const {
    control,
    watch,
    register,
    setValue,
    reset,
    handleSubmit,
    formState: { errors }
  } = useForm<RegisterMediaCaseType>({
    defaultValues: {
      industriesNum1: 0,
      industriesNum2: 0,
      industriesNum3: 0
    },
    resolver: yupResolver(mediaCaseShema)
  });

  const createCheckboxBooleanArray = (
    data?: number[],
    length?: number
  ): boolean[] => {
    if (!length || !data) return [];

    const newArray = Array(length).fill(false) as boolean[];
    data.forEach((element: number) => {
      newArray[element] = true;
    });

    return newArray;
  };

  useEffect(() => {
    void (async () => {
      const mediaRef = doc(mediasRef, id);
      const mediaDoc = await getDoc(mediaRef);
      const mediaData = mediaDoc.data() as MediaType;
      setMediaCompanyId(mediaData?.companyId);
      reset({
        clientsBenefits: mediaData?.clientsBenefits,
        continuousFactors1: mediaData?.continuousFactors?.[0],
        continuousFactors2: mediaData?.continuousFactors?.[1],
        continuousFactors3: mediaData?.continuousFactors?.[2],
        industriesNum1: mediaData?.industries?.[0]?.industriesNum ?? 0,
        industriesNum2: mediaData?.industries?.[1]?.industriesNum ?? 0,
        industriesNum3: mediaData?.industries?.[2]?.industriesNum ?? 0,
        industriesOther1: mediaData?.industries?.[0]?.industriesOther ?? null,
        industriesOther2: mediaData?.industries?.[1]?.industriesOther ?? null,
        industriesOther3: mediaData?.industries?.[2]?.industriesOther ?? null,
        industriesPercentage1: mediaData?.industries?.[0]?.percentage,
        industriesPercentage2: mediaData?.industries?.[1]?.percentage,
        industriesPercentage3: mediaData?.industries?.[2]?.percentage,
        mainKpi: createCheckboxBooleanArray(mediaData?.mainKpi, 10),
        mainKpiOther: mediaData?.mainKpiOther,
        mediaMenuUrl: mediaData?.mediaMenuUrl
      });
    })();
  }, []);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('industriesNum1') === undefined ||
        (watch('industriesNum1') === select.other &&
          Boolean(!watch('industriesOther1'))) ||
        watch('industriesNum1') === undefined ||
        (watch('industriesNum1') === select.other &&
          Boolean(!watch('industriesOther1'))) ||
        watch('industriesNum1') === undefined ||
        (watch('industriesNum1') === select.other &&
          Boolean(!watch('industriesOther1'))) ||
        (watch('mainKpi.99') && Boolean(!watch('mainKpiOther'))) ||
        watch('clientsBenefits') === undefined ||
        watch('continuousFactors1') === undefined ||
        watch('continuousFactors2') === undefined ||
        watch('continuousFactors3') === undefined ||
        Boolean(errors.industriesNum1) ||
        (watch('industriesNum1') === select.other &&
          Boolean(errors.industriesOther1)) ||
        Boolean(errors.industriesNum2) ||
        (watch('industriesNum2') === select.other &&
          Boolean(errors.industriesOther2)) ||
        Boolean(errors.industriesNum3) ||
        (watch('industriesNum3') === select.other &&
          Boolean(errors.industriesOther3)) ||
        Boolean(errors.mainKpi) ||
        (watch('mainKpi.99') && Boolean(errors.mainKpiOther)) ||
        Boolean(errors.clientsBenefits) ||
        Boolean(errors.continuousFactors1) ||
        Boolean(errors.continuousFactors2) ||
        Boolean(errors.continuousFactors3) ||
        Boolean(errors.mediaMenuUrl)
      );
    }
    return false;
  };

  const undoToIssue = async (): Promise<void> => {
    if (id) {
      setLoading(true);
      try {
        await editMedia4(watch, id);
        navigate(`/publisher/mypage/media/${id}/edit3`);
      } catch (e) {
        throwError(e);
      }
    }
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      if (id) {
        await editMedia4(watch, id);
        navigate(`/publisher/mypage/media/complete`);
      }
    } catch (e) {
      throwError(e);
    }
  };

  useEffect(() => {
    watch('mainKpiOther') && setValue('mainKpi.99', true);
  }, [watch('mainKpiOther')]);

  useEffect(() => {
    !watch('mainKpi.99') && setValue('mainKpiOther', null);
  }, [watch('mainKpi.99')]);

  return (
    <>
      <Typography
        component="div"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'static',
          width: '100%'
        }}
      >
        <Typography sx={styles.mypageHeader} component="div">
          <Typography sx={styles.mypageHeaderTitle}>
            {t('mypage.mediaRegister')}
          </Typography>
          <Button
            disabled={buttonDisabledFlag()}
            variant="contained"
            sx={primaryButton}
            onClick={() => {
              void (async () => {
                setFirstSubmit(true);
                await handleSubmit(async () => await onSubmit())();
              })();
            }}
          >
            {t('publisherMediaRegisterEdit.submit')}
          </Button>
        </Typography>

        <CustomLoading loading={loading} />
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          sx={{ marginBottom: '24px', marginTop: '24px' }}
        >
          <Grid>
            <Card sx={[styles.cardSize, styles.cardBorder]}>
              <CardContent sx={styles.cardContentPadding}>
                <CustomStepper steps={steps} activeStep={3} />
                <Typography
                  component="div"
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '16px'
                  }}
                >
                  <Typography sx={styles.title} component="div">
                    {t('publisherMediaRegisterEdit.step4')}
                  </Typography>
                </Typography>
                <Typography component="div" sx={styles.flex}>
                  <Typography sx={styles.grayPlaceholderText}>
                    {t('publisherMediaRegisterEdit.productTop3')}
                  </Typography>
                  <Typography sx={styles.isRequiredText}>
                    {t('form.required')}
                  </Typography>
                </Typography>
                <Typography sx={styles.text}>
                  {t('publisherMediaRegisterEdit.top1')}
                </Typography>

                <Controller
                  render={({ field }) => (
                    <CustomSelectField
                      {...field}
                      labelText={t(
                        'publisherMediaRegisterEdit.mainClientProduct'
                      )}
                      isRequired
                      requiredText={t('form.required')}
                      list={industryList}
                      formMb="narrow"
                      error={Boolean(errors.industriesNum1)}
                      helperText={errors.industriesNum1?.message}
                    />
                  )}
                  control={control}
                  name="industriesNum1"
                />

                {watch('industriesNum1') === select.other && (
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={
                          watch('industriesNum1') === select.other &&
                          Boolean(errors.industriesOther1)
                        }
                        helperText={errors.industriesOther1?.message}
                        isRequired={false}
                        sx={{ marginTop: '0px' }}
                      />
                    )}
                    control={control}
                    name="industriesOther1"
                  />
                )}

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      labelText={t('publisherMediaRegisterEdit.paymentPercent')}
                      isRequired={false}
                      width="half"
                      placeholder={'00%'}
                      type="number"
                      error={Boolean(errors.industriesPercentage1)}
                      helperText={errors.industriesPercentage1?.message}
                    />
                  )}
                  control={control}
                  name="industriesPercentage1"
                />

                <Typography sx={styles.text}>
                  {t('publisherMediaRegisterEdit.top2')}
                </Typography>

                <Controller
                  render={({ field }) => (
                    <CustomSelectField
                      {...field}
                      labelText={t(
                        'publisherMediaRegisterEdit.mainClientProduct'
                      )}
                      isRequired
                      requiredText={t('form.required')}
                      list={industryList}
                      formMb="narrow"
                      error={Boolean(errors.industriesNum2)}
                      helperText={errors.industriesNum2?.message}
                    />
                  )}
                  control={control}
                  name="industriesNum2"
                />

                {watch('industriesNum2') === select.other && (
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={
                          watch('industriesNum2') === select.other &&
                          Boolean(errors.industriesOther2)
                        }
                        helperText={errors.industriesOther2?.message}
                        isRequired={false}
                        sx={{ marginTop: '0px' }}
                      />
                    )}
                    control={control}
                    name="industriesOther2"
                  />
                )}

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      labelText={t('publisherMediaRegisterEdit.paymentPercent')}
                      isRequired={false}
                      width="half"
                      placeholder={'00%'}
                      type="number"
                      error={Boolean(errors.industriesPercentage2)}
                      helperText={errors.industriesPercentage2?.message}
                    />
                  )}
                  control={control}
                  name="industriesPercentage2"
                />

                <Typography sx={styles.text}>
                  {t('publisherMediaRegisterEdit.top3')}
                </Typography>

                <Controller
                  render={({ field }) => (
                    <CustomSelectField
                      {...field}
                      labelText={t(
                        'publisherMediaRegisterEdit.mainClientProduct'
                      )}
                      isRequired
                      requiredText={t('form.required')}
                      list={industryList}
                      formMb="narrow"
                      error={Boolean(errors.industriesNum3)}
                      helperText={errors.industriesNum3?.message}
                    />
                  )}
                  control={control}
                  name="industriesNum3"
                />

                {watch('industriesNum3') === select.other && (
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={
                          watch('industriesNum3') === select.other &&
                          Boolean(errors.industriesOther3)
                        }
                        helperText={errors.industriesOther3?.message}
                        isRequired={false}
                        sx={{ marginTop: '0px' }}
                      />
                    )}
                    control={control}
                    name="industriesOther3"
                  />
                )}

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      labelText={t('publisherMediaRegisterEdit.paymentPercent')}
                      isRequired={false}
                      width="half"
                      placeholder={'00%'}
                      type="number"
                      error={Boolean(errors.industriesPercentage3)}
                      helperText={errors.industriesPercentage3?.message}
                    />
                  )}
                  control={control}
                  name="industriesPercentage3"
                />

                <CustomCheckbox
                  labelText={t('publisherMediaRegisterEdit.kpi')}
                  isRequired={true}
                  requiredText={t('form.required')}
                  list={mainKpi()}
                  error={Boolean(errors.mainKpi)}
                  helperText={errors.mainKpi?.message}
                  control={control}
                  watchMediaCase={watch}
                  registerMediaCase={register}
                  formMb="narrow"
                  name="mainKpi"
                />

                <Typography component="div" sx={{ marginLeft: '25px' }}>
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        isRequired={false}
                        multiline
                        rows={3}
                        fieldType="textareaRow3"
                        width="other"
                        error={Boolean(errors.mainKpiOther)}
                        helperText={errors.mainKpiOther?.message}
                      />
                    )}
                    control={control}
                    name="mainKpiOther"
                  />
                </Typography>

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      isRequired
                      requiredText={t('form.required')}
                      labelText={t('publisherMediaRegisterEdit.meritClient')}
                      multiline
                      rows={7}
                      fieldType="textareaRow7"
                      width="article"
                      error={Boolean(errors.clientsBenefits)}
                      helperText={errors.clientsBenefits?.message}
                      guideText={t('guideText.registerMedia.clientsBenefits')}
                    />
                  )}
                  control={control}
                  name="clientsBenefits"
                />

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      isRequired
                      requiredText={t('form.required')}
                      labelText={t('publisherMediaRegisterEdit.keepFactor1')}
                      multiline
                      rows={7}
                      fieldType="textareaRow7"
                      width="article"
                      error={Boolean(errors.continuousFactors1)}
                      helperText={errors.continuousFactors1?.message}
                      guideText={t(
                        'guideText.registerMedia.continuousFactors1'
                      )}
                    />
                  )}
                  control={control}
                  name="continuousFactors1"
                />

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      isRequired
                      requiredText={t('form.required')}
                      labelText={t('publisherMediaRegisterEdit.keepFactor2')}
                      multiline
                      rows={7}
                      fieldType="textareaRow7"
                      width="article"
                      error={Boolean(errors.continuousFactors2)}
                      helperText={errors.continuousFactors2?.message}
                    />
                  )}
                  control={control}
                  name="continuousFactors2"
                />

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      isRequired
                      requiredText={t('form.required')}
                      labelText={t('publisherMediaRegisterEdit.keepFactor3')}
                      multiline
                      rows={7}
                      fieldType="textareaRow7"
                      width="article"
                      error={Boolean(errors.continuousFactors3)}
                      helperText={errors.continuousFactors3?.message}
                    />
                  )}
                  control={control}
                  name="continuousFactors3"
                />

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      labelText={t('publisherMediaRegisterEdit.cases')}
                      isRequired={false}
                      width="article"
                      placeholder={'URL'}
                      type="url"
                      error={Boolean(errors.mediaMenuUrl)}
                      helperText={errors.mediaMenuUrl?.message}
                    />
                  )}
                  control={control}
                  name="mediaMenuUrl"
                />

                <CardActions sx={[styles.textMb, styles.submit]}>
                  <Grid container sx={styles.submitCol}>
                    <Grid item>
                      <Button
                        disabled={buttonDisabledFlag()}
                        variant="outlined"
                        fullWidth
                        onClick={() => {
                          void (async () => {
                            setFirstSubmit(true);
                            await handleSubmit(
                              async () => await undoToIssue()
                            )();
                          })();
                        }}
                        sx={styles.undoButton}
                      >
                        <Typography sx={{ fontSize: '14px' }}>
                          {t('passwordReset.back')}
                        </Typography>
                      </Button>
                    </Grid>
                    <Grid item flex={1}>
                      <Button
                        disabled={buttonDisabledFlag()}
                        fullWidth
                        sx={[primaryButtonBackground, styles.submitButton]}
                        onClick={() => {
                          void (async () => {
                            setFirstSubmit(true);
                            await handleSubmit(async () => await onSubmit())();
                          })();
                        }}
                      >
                        <Typography
                          component="div"
                          color="white"
                          sx={{ fontSize: 15 }}
                        >
                          {t('publisherMediaRegisterEdit.submit')}
                        </Typography>
                      </Button>
                    </Grid>
                  </Grid>
                </CardActions>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <CustomSnackbar
          open={open}
          autoHideDuration={6000}
          onClose={() => setOpen(false)}
          text={errorText}
        />
      </Typography>
    </>
  );
};
export default MediaRegisterCaseEditCard;
